/**
 * autofit-text — ставит МАКСИМАЛЬНЫЙ font-size, при котором текст ещё помещается
 * в свой блок, ИЗМЕРЯЯ реальную ширину слов (canvas measureText), а не подбирая
 * коэффициенты руками (owner-directive 2026-05-29: «не ты прикидывал, а в скрипте
 * вычислялось … максимально большой, который возможен на этом разрешении»).
 *
 * Перенос — ТОЛЬКО по словам; разрыв слова по буквам ЗАПРЕЩЁН (owner-rule
 * 2026-05-29, см. memory/feedback_no_midword_letter_break.md). Поэтому ограничение
 * по ширине = «самое длинное слово должно влезть в доступную ширину». Самый большой
 * шрифт, при котором длиннейшее слово ≤ доступной ширины, и есть искомый размер.
 *
 * Замер через canvas (а не getBoundingClientRect-итерации) → без layout-thrash.
 * Шрифт меряется в реальной гарнитуре элемента, поэтому результат точный в каждом
 * движке (мелкие межбраузерные различия размера — это presentation, не геймплей,
 * на детерминизм движка/реплеев не влияет).
 */

import { VIEWPORT_APPLIED_EVENT } from './viewport-sizer.js';

const measureCtx: CanvasRenderingContext2D | null = (() => {
  if (typeof document === 'undefined') return null;
  const c = document.createElement('canvas');
  return c.getContext('2d');
})();

/** Reference px at which words are measured; result scales linearly from it. */
const REF_PX = 100;

/**
 * Измеритель ширины строки `text` в шрифте `font` (как `ctx.font`-строка) при REF_PX.
 * Дефолт — canvas (`measureCtx`). Инъекция нужна тестам (node-env без canvas):
 * измерение ширины — ЕДИНСТВЕННАЯ не-чистая часть; выбор min по группе и линейный
 * scale от REF_PX — детерминированная арифметика, её можно проверить без браузера.
 */
export type MeasureWidth = (text: string, font: string) => number;

/** Дефолтный измеритель на canvas (null, если canvas недоступен — node без DOM). */
const canvasMeasure: MeasureWidth | null = measureCtx
  ? (text, font) => {
      measureCtx.font = font;
      return measureCtx.measureText(text).width;
    }
  : null;

export interface FitOptions {
  /**
   * Инъекция измерителя ширины (тесты / нестандартная гарнитура). По умолчанию —
   * canvas `measureText`. Передаётся ТОЛЬКО из тестов; продакшен-потребители его не
   * задают (берётся canvas).
   */
  measure?: MeasureWidth;
  /** Жёсткий верхний предел (px). Опционально. */
  maxPx?: number;
  /** Нижний предел (px), чтобы шрифт не уходил в 0. По умолчанию 6px. */
  minPx?: number;
  /**
   * Не превышать дизайн-размер из стайлшита (font-size, который дал бы CSS без
   * нашего inline-override). true для статистики — она своего дизайн-размера и лишь
   * УМЕНЬШАЕТСЯ при нехватке ширины, а не раздувается.
   */
  capToDesign?: boolean;
  /**
   * Режим подбора:
   *  · 'word' (по умолчанию) — лейбл может переноситься по словам; ограничение =
   *    «длиннейшее СЛОВО влезает в ширину». Для заголовка/статистики.
   *  · 'line' — ВЕСЬ лейбл в ОДНУ строку; ограничение = и по ширине (весь текст),
   *    и по ВЫСОТЕ блока (min-height, чтобы строка не вылезла за высоту кнопки).
   *    Растёт до заполнения кнопки, не превышая её высоту. Для кнопок.
   */
  mode?: 'word' | 'line';
  /**
   * Дельта заполнения (0…1) для режима 'line': итоговый размер = fillRatio ×
   * (максимум, который влезает в блок по ширине И высоте). Единственная ручка для
   * «насколько крупным текст заполняет кнопку»: 1 — заполняет целиком, 0.7 — 70%.
   * Расчёт по-прежнему ОТ КНОПКИ (ширина+высота), дельта лишь масштабирует пропорцию
   * (owner-directive 2026-05-29: «ориентируется на размер кнопки, но с дельтой,
   * меняем только дельту»). По умолчанию 1 (без изменений для прочих потребителей).
   */
  fillRatio?: number;
  /**
   * Элемент, от высоты которого берётся СТАБИЛЬНАЯ опора (min-height/clientHeight)
   * в mode:'line'. По умолчанию — сам измеряемый `el`.
   *
   * ЗАЧЕМ (owner-fix 2026-06-12 — КОРНЕВОЙ БАГ идемпотентности): когда ширина меряется
   * от ТЕКСТ-элемента (напр. `.pill-button-label`, flex 1 1 0), а у него НЕТ своего
   * `min-height`, высотная опора падала на `el.clientHeight` = КОНТЕНТ-высота = текущий
   * шрифт (который autofit сам только что поставил) → опора «резиновая», размер
   * НЕидемпотентен: ужавшись на узком, НЕ растёт обратно при resize-вверх (залипал
   * мелким; свежее 4K-открытие норм, живой ресайз снизу-вверх — залипал). Передача
   * КОНТРОЛА (кнопка/таб с реальным `min-height: --btn-h`) делает высотную опору
   * стабильной в ОБЕ стороны. Ширину по-прежнему меряем от `el` (текст-элемента),
   * letter-spacing/padding — тоже от `el`. skill §8.10-D.
   */
  heightEl?: HTMLElement;
  /**
   * Опциональный CSS-класс «упёрлись в floor И текст всё равно не влезает». Когда
   * задан, fitText после применения размера ставит этот класс на элемент, если шрифт
   * сжат до minPx, а текст ВСЁ РАВНО шире блока (`scrollWidth > clientWidth`), и
   * снимает его, когда текст помещается. Нужен, чтобы навешивать НЕ-обрезающий
   * mask-fade ТОЛЬКО на реально переполненный текст (owner-rule 2026-06-04: «запрещён
   * перенос и …»; угасание — последний рубеж), а короткие/влезающие надписи оставлять
   * без угасания хвоста. Без флага поведение не меняется (прочие потребители
   * — compact-rail и т.п. — его не передают).
   */
  clampClass?: string;
}

/** Цель авто-подбора: селектор + его опции. */
export interface FitTarget extends FitOptions {
  selector: string;
  /**
   * Единый размер на всю группу: вычислить max-fit каждого элемента и применить
   * ко ВСЕМ МИНИМУМ (чтобы все влезли и были одинаковыми). Для группы кнопок —
   * иначе короткий лейбл вырос бы крупнее длинного, кнопки разной величины.
   */
  uniform?: boolean;
  /**
   * Применять цель ТОЛЬКО в указанной раскладке (`stacked` / `rail`). Без флага —
   * в обеих. Нужно, когда один и тот же элемент подбирается по-разному по
   * раскладкам: напр. заголовок в портрете (stacked) — в ОДНУ строку (line, ужать
   * по ширине), а в ландшафте (rail) — крупный с переносом по словам. fitAll
   * сверяет с `document.documentElement.dataset.layout` при каждом замере, поэтому
   * при смене раскладки активной становится нужная цель.
   */
  onlyLayout?: 'stacked' | 'rail';
}

/**
 * Вычислить максимальный font-size (px) для элемента, НЕ применяя. null — если
 * замер невозможен. Логика см. fitText.
 *
 * EXPORTED — используется как измеряемый триггер раскладки (без подобранных
 * порогов): напр. compact-rail сравнивает max-fit лейбла кнопки с floor читаемости
 * (`FS_BUTTON_MIN_PX`) — если лейбл влезает только мельче floor'а, кнопка
 * сворачивается в компактную форму (docs/specs/board-compact-rail.md AC-4).
 * Передавай `{ minPx: 0 }` чтобы получить СЫРОЙ max-fit (без клампа к floor).
 */
export function computeFitSize(el: HTMLElement, opts: FitOptions = {}): number | null {
  const measure = opts.measure ?? canvasMeasure;
  if (!measure) return null;
  const text = (el.textContent ?? '').trim();
  if (!text) return null;
  // ФАНТОМ-ЗАМЕР (i18n no-shift, skill §8.10-B): элемент, чей видимый текст по ходу
  // жизни СВАПается на ДРУГОЙ лейбл (напр. кнопка «Играть»→«проверяю…»), несёт ВСЕ
  // свои возможные состояния в `data-autofit-measure-as` (кандидаты через `\n`).
  // Подбор меряет САМЫЙ ШИРОКИЙ из {текущий textContent, …кандидаты} → uniform-шрифт
  // садится так, что ЛЮБОЕ состояние влезает БЕЗ refit при свапе (no-shift в каждой
  // локали, где pending-лейбл длиннее базового: tr/vi/th/pt/es/fr). Высота/гарнитура/
  // padding — по-прежнему от самого `el`; меняется ТОЛЬКО набор измеряемых строк.
  // `?.` — node-env DOM-стабы тестов могут не иметь `dataset` (фантом им не нужен).
  const phantom = el.dataset?.['autofitMeasureAs'];
  const texts =
    phantom != null
      ? [text, ...phantom.split('\n').map((s) => s.trim()).filter((s) => s.length > 0)]
      : [text];
  // capToDesign: на время замера снимаем наш inline font-size, чтобы прочитать
  // дизайн-размер из стайлшита (responsive — он пересчитывается с вьюпортом).
  if (opts.capToDesign) el.style.fontSize = '';
  const cs = getComputedStyle(el);
  const designPx = parseFloat(cs.fontSize) || 0;
  const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
  const avail = el.clientWidth - padX;
  if (avail <= 0) return null;
  const ls = parseFloat(cs.letterSpacing) || 0; // абсолютный px между буквами
  const font = `${cs.fontStyle} ${cs.fontWeight} ${REF_PX}px ${cs.fontFamily}`;

  let size = opts.maxPx ?? Infinity;

  if (opts.mode === 'line') {
    // ВЕСЬ лейбл в одну строку: ограничение по ширине (весь текст) И по высоте.
    // Меряем КАЖДОГО кандидата (фантом-член) и берём САМЫЙ строгий (min по ширине) —
    // итог влезает в любое состояние лейбла.
    for (const candidate of texts) {
      const glyphW = measure(candidate, font); // весь текст при REF_PX
      const chars = [...candidate].length;
      if (glyphW > 0) {
        const byWidth = ((avail - chars * ls) * REF_PX) / glyphW;
        if (byWidth < size) size = byWidth;
      }
    }
    // По высоте: одна строка (line-height·font) должна влезть в высоту блока
    // (min-height — стабильная опора, напр. --btn-h у кнопки) минус vertical padding.
    const fontPx = parseFloat(cs.fontSize) || REF_PX;
    const lhPx = parseFloat(cs.lineHeight) || fontPx * 1.2;
    const ratio = fontPx > 0 ? lhPx / fontPx : 1.2; // множитель line-height (стабилен)
    // Высотная опора — от КОНТРОЛА (opts.heightEl: кнопка/таб с реальным min-height),
    // если задан; иначе от самого `el`. Берём min-height/padding КОНТРОЛА — это
    // СТАБИЛЬНАЯ величина (не зависит от текущего шрифта), поэтому autofit идемпотентен
    // на resize в ОБЕ стороны (owner-fix 2026-06-12 — иначе клиент-высота label =
    // текущий шрифт → залипание мелким при resize-вверх).
    const hEl = opts.heightEl ?? el;
    const hcs = hEl === el ? cs : getComputedStyle(hEl);
    const padY = (parseFloat(hcs.paddingTop) || 0) + (parseFloat(hcs.paddingBottom) || 0);
    const heightRef = parseFloat(hcs.minHeight) || hEl.clientHeight;
    const availH = heightRef - padY;
    if (availH > 0 && ratio > 0) {
      const byHeight = availH / ratio;
      if (byHeight < size) size = byHeight;
    }
    // Дельта заполнения: размер = fillRatio × (максимум, влезающий в кнопку).
    // Расчёт ОТ КНОПКИ (size = min(maxPx, byWidth, byHeight)); дельта масштабирует
    // пропорцию (одна ручка «насколько крупно»). Default 1.
    if (Number.isFinite(size)) size *= opts.fillRatio ?? 1;
    // capToDesign — ПОТОЛОК на ИТОГ (после дельты): размер не превышает дизайн-
    // размер из стайлшита (--fs-button и т.п.). owner-directive 2026-05-30: «максимум
    // 0.7 от кнопки — но не больше дизайна»: на больших кнопках ПК (cell·1.3 ≈ 80px)
    // дельта 0.7 давала ~31px (слишком крупно) → теперь упирается в --fs-button.
    // Для fillRatio=1 (заголовок/статистика) порядок не меняет результат.
    if (opts.capToDesign && designPx > 0 && size > designPx) size = designPx;
  } else {
    // По словам (перенос разрешён): длиннейшее СЛОВО должно влезть в ширину.
    // Перебираем слова ВСЕХ кандидатов (фантом-член) — самое длинное связывает размер.
    if (opts.capToDesign && designPx > 0) size = Math.min(size, designPx);
    for (const candidate of texts) {
      for (const word of candidate.split(/\s+/)) {
        if (!word) continue;
        const glyphW = measure(word, font); // при REF_PX, без letter-spacing
        if (glyphW <= 0) continue;
        const chars = [...word].length;
        // size, при котором это слово ровно влезает в avail с учётом letter-spacing:
        const perWord = ((avail - chars * ls) * REF_PX) / glyphW;
        if (perWord < size) size = perWord;
      }
    }
  }

  if (!Number.isFinite(size)) return null;
  return Math.max(opts.minPx ?? 6, size);
}

/**
 * Ширина (px) ВСЕГО `text` в ОДНУ строку при размере `fontPx`, в гарнитуре `refEl`
 * (читается из его computedStyle — работает даже если refEl `display:none`, т.к.
 * шрифт всё равно резолвится). Учитывает letter-spacing. 0 — если замер невозможен
 * (fail-open у вызывающего).
 *
 * Используется как ИЗМЕРЯЕМЫЙ триггер раскладки (без подобранных порогов) для кнопок
 * в режиме `'line'` (весь лейбл в одну строку): compact-rail сравнивает
 * `textWidthPx(label, FS_BUTTON_MIN, btn)` с доступной шириной панели — если весь
 * лейбл не влезает в одну строку даже при floor-шрифте, панель сворачивается в
 * компактную форму (docs/specs/board-compact-rail.md AC-4).
 */
export function textWidthPx(text: string, fontPx: number, refEl: HTMLElement): number {
  if (!canvasMeasure) return 0;
  const cs = getComputedStyle(refEl);
  const ls = parseFloat(cs.letterSpacing) || 0;
  const font = `${cs.fontStyle} ${cs.fontWeight} ${REF_PX}px ${cs.fontFamily}`;
  const scale = fontPx / REF_PX;
  return canvasMeasure(text.trim(), font) * scale + [...text.trim()].length * ls;
}

/**
 * Член group-uniform группы. Либо сам текст-элемент (тогда ширина И высотная опора
 * берутся от него), либо пара `{ el, heightEl }`: ШИРИНА от текст-элемента `el` (напр.
 * `.pill-button-label`, flex 1 1 0), а СТАБИЛЬНАЯ высотная опора — от КОНТРОЛА
 * `heightEl` (кнопка/таб с min-height --btn-h). См. FitOptions.heightEl.
 */
export type GroupMember = HTMLElement | { readonly el: HTMLElement; readonly heightEl?: HTMLElement };

/**
 * Нормализовать члена группы к паре { el, heightEl }. Дискриминатор — наличие поля
 * `el` (а НЕ `instanceof HTMLElement`: в node-env тестов члены — DOM-стабы, для них
 * `instanceof` ложно-false → пара ошибочно бы развернулась как `.el`). Член-пара
 * всегда имеет собственное поле `el`; голый текст-элемент — нет.
 */
function isPair(m: GroupMember): m is { readonly el: HTMLElement; readonly heightEl?: HTMLElement } {
  return typeof m === 'object' && m !== null && 'el' in m;
}
function memberEl(m: GroupMember): HTMLElement {
  return isPair(m) ? m.el : m;
}
function memberHeightEl(m: GroupMember): HTMLElement | undefined {
  return isPair(m) ? m.heightEl : undefined;
}

/**
 * GROUP-UNIFORM подбор: ОДИН измеренный размер для группы родственных текстовых
 * контролов (сегмент-табы «Вход/Регистрация», ряд кнопок, 3 провайдер-кнопки) =
 * МИНИМУМ, вписывающийся в САМЫЙ ДЛИННЫЙ лейбл группы (owner-law 2026-06-12,
 * skill §8.10-B). Один лейбл вынудил уменьшение → ВСЕ в группе уменьшаются до того
 * же размера (одинаковый смысл = один размер).
 *
 * ПОЧЕМУ ОТДЕЛЬНАЯ ФУНКЦИЯ (а не только `uniform`-флаг в FitTarget): группа часто =
 * программно созданные элементы (кнопки сегмента), а не общий CSS-селектор. Прямой
 * приём списка `members` исключает дубль логики «измерь каждый → min → примени всем»
 * по экранам — это ПРИМИТИВ (owner-directive «через модули»), а `fitAll` `uniform`
 * делегирует сюда.
 *
 * РАЗНОТИПНАЯ группа (owner-law 2026-06-12 — плашка владеет правилом): члены могут быть
 * РАЗНЫХ контролов (провайдер-кнопка, где текст = вложенный label; сегмент-таб; CTA, где
 * текст = сама кнопка). Поэтому член = `GroupMember`: ШИРИНА всегда от текст-элемента, а
 * высотная опора — от его КОНТРОЛА (`heightEl`), стабильная (см. FitOptions.heightEl —
 * фикс идемпотентности resize-вверх). Каждый меряется от СВОЕГО элемента, размер = min
 * по группе, применяется ВСЕМ → «равны ПОТОМУ ЧТО велит плашка».
 *
 * i18n-КРИТИЧНО: размер всегда ИЗМЕРЯЕТСЯ (не подгоняется под слово) → в другом языке
 * (где лейбл длиннее) группа сама ужмётся, текст НЕ вылезет за свой элемент. Статичный
 * font-size / padding-«подгон» на grouped-лейблах PROHIBITED (skill §8.10-B / AP-50).
 *
 * Возвращает применённый ко всем размер (px) или null, если ни один элемент не
 * измерился (canvas недоступен / нет видимой ширины) — тогда font-size не трогается
 * (CSS-fallback держит дизайн-размер).
 */
export function fitGroupUniform(
  members: readonly GroupMember[],
  opts: FitOptions = {},
): number | null {
  let min = Infinity;
  for (const m of members) {
    const hEl = memberHeightEl(m);
    const s = computeFitSize(memberEl(m), hEl ? { ...opts, heightEl: hEl } : opts);
    if (s != null && s < min) min = s;
  }
  if (!Number.isFinite(min)) return null;
  for (const m of members) memberEl(m).style.fontSize = `${min}px`;
  // Опциональный floor-overflow класс: когда уникальный размер группы упёрся в floor
  // (minPx) И конкретный лейбл ОДНОЙ СТРОКОЙ ВСЁ РАВНО шире своего блока — ставим класс
  // НЕ-обрезающего последнего рубежа (перенос по словам в CSS) ТОЛЬКО на переполненные
  // члены (короткие — без переноса). Нужно для i18n-длинных лейблов segmented (it/pt/th
  // «Доска дня»), не влезающих даже на 13px floor: skill §0.A P1 (crop PROHIBITED).
  //
  // ОВЕРФЛОУ МЕРЯЕМ ПО ОДНОЙ СТРОКЕ ЧЕРЕЗ CANVAS (textWidthPx), НЕ по DOM scrollWidth:
  // когда класс включает CSS-перенос, DOM scrollWidth схлопывается к ширине самого
  // длинного СЛОВА (< clientWidth) → проверка по scrollWidth сняла бы класс → revert к
  // nowrap → снова overflow → ОСЦИЛЛЯЦИЯ. Канвас-ширина всего лейбла одной строкой не
  // зависит от текущего white-space → детектор стабилен (идемпотентен в обе стороны).
  if (opts.clampClass) {
    const floor = opts.minPx ?? 6;
    const atFloor = min <= floor + 0.5; // +0.5 — допуск округления (как в fitText)
    for (const m of members) {
      const el = memberEl(m);
      const cs = getComputedStyle(el);
      const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      const avail = el.clientWidth - padX;
      const oneLineW = textWidthPx(el.textContent ?? '', min, el);
      const overflowing = oneLineW > avail + 1;
      el.classList.toggle(opts.clampClass, atFloor && overflowing);
    }
  }
  return min;
}

/**
 * Подобрать font-size элемента под его блок (largest-fits), измерением.
 * 'word' (по умолчанию) — длиннейшее слово влезает в ширину (перенос разрешён);
 * 'line' — весь лейбл в одну строку, по ширине И высоте блока.
 */
export function fitText(el: HTMLElement, opts: FitOptions = {}): void {
  const size = computeFitSize(el, opts);
  if (size != null) el.style.fontSize = `${size}px`;
  // Опциональный floor-overflow класс: ставим, только когда шрифт уже на нижнем
  // пределе (minPx) И текст всё равно не влезает (scrollWidth > clientWidth) — тогда
  // НЕ-обрезающий mask-fade в CSS уместен. Иначе снимаем (короткий/влезающий текст не
  // должен угасать хвостом). Снимаем и при невозможности замера (size==null), чтобы
  // не залипал устаревший класс. floor = opts.minPx ?? 6 (как в computeFitSize).
  if (opts.clampClass) {
    const floor = opts.minPx ?? 6;
    const atFloor = size != null && size <= floor + 0.5; // +0.5 — допуск на округление
    // Оверфлоу по ОДНОЙ строке через canvas (textWidthPx), НЕ по DOM scrollWidth: когда
    // clampClass включает CSS-перенос (заголовок .screen-title.is-clamped), scrollWidth
    // схлопывается к ширине строки → проверка по нему сняла бы класс → revert к nowrap →
    // ОСЦИЛЛЯЦИЯ. Канвас-ширина целого лейбла не зависит от white-space → детектор
    // стабилен. Для mask-клампов (ники лидерборда — текст НЕ переносится) результат
    // эквивалентен прежнему scrollWidth-чеку (масштаб не меняет геометрию строки).
    const cs = getComputedStyle(el);
    const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    const avail = el.clientWidth - padX;
    const oneLineW = size != null ? textWidthPx(el.textContent ?? '', size, el) : 0;
    const overflowing = oneLineW > avail + 1;
    el.classList.toggle(opts.clampClass, atFloor && overflowing);
  }
}

/** Подобрать шрифт для всех элементов целей внутри root. */
export function fitAll(root: ParentNode, targets: readonly FitTarget[]): void {
  // Текущая раскладка (для целей с onlyLayout). Читается при КАЖДОМ замере, поэтому
  // смена stacked↔rail переключает активную цель автоматически (autofit re-fit'ит
  // на VIEWPORT_APPLIED_EVENT / resize).
  const layout =
    typeof document !== 'undefined' ? document.documentElement.dataset['layout'] : undefined;
  for (const { selector, uniform, onlyLayout, ...opts } of targets) {
    if (onlyLayout && layout && onlyLayout !== layout) continue;
    const els = [...root.querySelectorAll<HTMLElement>(selector)];
    if (uniform) {
      // Единый размер на группу = min(max-fit каждого) → все влезают и одинаковы.
      // Делегируем примитиву fitGroupUniform (один источник правды group-mode).
      fitGroupUniform(els, opts);
    } else {
      for (const el of els) fitText(el, opts);
    }
  }
}

export interface AutofitHandle {
  /** Принудительно пересчитать (напр. после смены текста). */
  refit(): void;
  /** Отключить наблюдение. */
  detach(): void;
}

/**
 * Привязать авто-подбор к набору элементов внутри root: первичный замер +
 * пересчёт на ресайз контейнера (ResizeObserver на root — меняется при ресайзе
 * вьюпорта/смене --rail-w) + после загрузки шрифтов (measureText точен только
 * когда гарнитура загружена). rAF-дебаунс против лишних замеров.
 */
export function attachAutofit(
  root: HTMLElement,
  targets: readonly FitTarget[],
): AutofitHandle {
  let raf = 0;
  let trailing = 0;
  const run = (): void => {
    raf = 0;
    fitAll(root, targets);
  };
  const schedule = (): void => {
    if (raf) return;
    raf = requestAnimationFrame(run);
  };
  // Пересчёт СЕЙЧАС + ещё раз после settle. При смене КЛАССА раскладки
  // (rail↔stacked) реверстка происходит не мгновенно: первый замер берёт ширину
  // блока ДО переверстки (кнопка осталась крупной из rail-раскладки), и без
  // trailing-замера остаётся устаревшей до ручного поворота (owner-incident
  // 2026-05-29: большой → 320×480 → кнопки крупные, помогал только поворот).
  const scheduleSettled = (): void => {
    schedule();
    if (trailing) clearTimeout(trailing);
    trailing = setTimeout(schedule, 160);
  };

  // Первичный замер + settle.
  scheduleSettled();

  // Пересчёт при изменении размеров контейнера (ресайз вьюпорта → меняется
  // ширина панели → меняются доступные ширины блоков текста).
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(scheduleSettled);
    ro.observe(root);
  }

  // ГЛАВНЫЙ триггер: пересчёт при КАЖДОМ применении геометрии сайзером (после
  // установки токенов). Ловит изменения, которые меняют только --cell-size/
  // --rail-w, но НЕ размер screen-root (ResizeObserver их пропускает) — это и был
  // баг «текст криво с первой загрузки, помогает только поворот». scheduleSettled
  // добавляет trailing-замер после переверстки при смене класса раскладки.
  const onGeom = (): void => scheduleSettled();
  if (typeof window !== 'undefined') {
    window.addEventListener(VIEWPORT_APPLIED_EVENT, onGeom);
  }

  // Пересчёт после загрузки шрифтов (до этого measureText даёт ширины fallback-
  // гарнитуры → размер мог быть неточным).
  if (typeof document !== 'undefined' && 'fonts' in document) {
    void document.fonts.ready.then(scheduleSettled);
  }

  return {
    refit: schedule,
    detach(): void {
      if (raf) cancelAnimationFrame(raf);
      if (trailing) clearTimeout(trailing);
      if (ro) ro.disconnect();
      if (typeof window !== 'undefined') {
        window.removeEventListener(VIEWPORT_APPLIED_EVENT, onGeom);
      }
    },
  };
}
